import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";
import { LEGAL_VERSION } from "@/lib/legal";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("cs_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cs_token", data.access_token);
    await fetchMe();
  }, [fetchMe]);

  const register = useCallback(async (email, password, name, acceptance) => {
    const { data } = await api.post("/auth/register", {
      email,
      password,
      name,
      legal_version: LEGAL_VERSION,
      privacy_notice_acknowledged: Boolean(acceptance?.privacy),
      beta_terms_accepted: Boolean(acceptance?.terms),
      age_professional_confirmed: Boolean(acceptance?.professional),
    });
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("cs_token");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser: fetchMe }),
    [user, loading, login, register, logout, fetchMe]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
